import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ButtonModule } from 'primeng/button';
import { PickListModule } from 'primeng/picklist';
import { TabsModule } from 'primeng/tabs';
import { NgxForm, getDeep, setDeep } from './ngx-form.types';
import { TestUserBadgeComponent } from '../test-user-badge/test-user-badge.component';

/**
 * Local replacement for @reflact/ngx-forms' FormComponent (rag-form): renders a form from a
 * NgxForm.FormField[] definition. Supports the field types actually used across this app —
 * text, switch, select, multiselect, picklist, template — not the full original type union.
 */
@Component({
  selector: 'app-form',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, ButtonModule, PickListModule, TabsModule, TestUserBadgeComponent],
  templateUrl: './form.component.html'
})
export class FormComponent implements OnInit, OnChanges {
  @Input({ required: true }) public data: any;
  @Input() public formFields: NgxForm.FormField[] = [];
  @Input() public type: 'edit' | 'create' = 'edit';
  @Input() public hideDeleteButton = false;
  /** Set to false when the caller renders its own save/delete buttons elsewhere (e.g. a p-dialog footer). */
  @Input() public showActions = true;
  /** Zeigt am Speichern-Button einen Ladezustand, solange der Aufrufer speichert. */
  @Input() public saving = false;
  @Input() public translatedLabels?: NgxForm.TranslatedLabels;
  /** Optional Tabs oberhalb der Felder (z.B. "Erscheinungsbild"/"Einstellungen") — bleibt leer,
   * wenn der Aufrufer keine Tabs braucht (Standardverhalten, ein einzelnes `.row` wie bisher).
   * Felder ohne passendes `tab` werden in keinem Tab angezeigt, siehe getFieldsForTab(). */
  @Input() public tabs: { id: string; label: string }[] = [];
  public activeTabId?: string;
  @Output() public submit = new EventEmitter<any>();
  @Output() public delete = new EventEmitter<any>();
  @ViewChild('formRef') public form!: NgForm;
  // Guards against a stray duplicate ngSubmit firing shortly after the real one (observed:
  // a second submit with a stale/incomplete `data` reaching the backend a few ms later).
  private submitLock = false;

  public ngOnInit(): void {
    this.activeTabId = this.tabs[0]?.id;
  }

  /** Ein neuer Datensatz (anderes Objekt in `data`) braucht frisch aus ihm abgeleitete PickList-
   * Source-/Target-Listen — sonst würde beim Wechsel auf einen anderen Datensatz noch der
   * gecachte Stand des vorherigen angezeigt, siehe getPicklistState(). */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.picklistState.clear();
    }
  }

  public getFieldsForTab(tabId: string): NgxForm.FormField[] {
    return this.formFields.filter(field => field.tab === tabId);
  }

  public getValue(field: NgxForm.FormField): any {
    return getDeep(this.data, field.fieldName);
  }

  public setValue(field: NgxForm.FormField, value: any): void {
    setDeep(this.data, field.fieldName, value);
  }

  public getLabel(field: NgxForm.FormField): string {
    return this.translatedLabels?.get(field.fieldName) ?? field.label ?? field.fieldName;
  }

  /** Zieht den Vergleichswert eines PickList-Items — `item[bindValue]` bei Objektlisten (z.B.
   * Kacheln), sonst das Item selbst (z.B. reine Rollen-Strings). */
  private getPicklistItemValue(field: NgxForm.FormField, item: any): any {
    const bindValue = field.options?.bindValue;
    return bindValue ? item?.[bindValue] : item;
  }

  public getPicklistItemLabel(field: NgxForm.FormField, item: any): string {
    const bindLabel = field.options?.bindLabel;
    return bindLabel ? item?.[bindLabel] : item;
  }

  /** Pro Feld gecachte Source-/Target-Arrays fürs PickList — WICHTIG: `p-picklist` ist intern
   * OnPush, `[source]`/`[target]` dürfen also NICHT bei jedem Change-Detection-Zyklus neu aus
   * `field.options.selectValues` gefiltert werden (das würde bei jedem Tick eine neue Array-
   * Referenz liefern und damit den internen Auswahl-/Drag-Zustand des PickLists zurücksetzen —
   * Klicken/Verschieben wirkte dadurch komplett wirkungslos). Wird nur neu aufgebaut, wenn sich
   * `selectValues` selbst ändert (z.B. nach async Nachladen), siehe getPicklistState(). */
  private picklistState = new Map<string, { selectValues: any[]; readonlyValues: any[]; source: any[]; target: any[] }>();

  private getPicklistState(field: NgxForm.FormField): { selectValues: any[]; readonlyValues: any[]; source: any[]; target: any[] } {
    const selectValues = field.options?.selectValues ?? [];
    const readonlyValues = field.options?.readonlyTargetValues?.(this.data) ?? [];
    const cached = this.picklistState.get(field.fieldName);
    if (cached && cached.selectValues === selectValues && cached.readonlyValues.length === readonlyValues.length && cached.readonlyValues.every((item, index) => item === readonlyValues[index])) {
      return cached;
    }
    const selected: any[] = this.getValue(field) ?? [];
    const state = {
      selectValues,
      readonlyValues,
      source: selectValues.filter((item: any) => !selected.includes(this.getPicklistItemValue(field, item))),
      target: [...readonlyValues, ...selectValues.filter((item: any) => selected.includes(this.getPicklistItemValue(field, item)))],
    };
    this.picklistState.set(field.fieldName, state);
    return state;
  }

  public getPicklistSource(field: NgxForm.FormField): any[] {
    return this.getPicklistState(field).source;
  }

  public getPicklistTarget(field: NgxForm.FormField): any[] {
    return this.getPicklistState(field).target;
  }

  public isReadonlyPicklistItem(field: NgxForm.FormField, item: any): boolean {
    return this.getPicklistState(field).readonlyValues.includes(item);
  }

  /** `p-picklist` mutiert bei Button-Klicks (moveRight/moveLeft/moveAllRight/moveAllLeft) die
   * source-/target-Arrays IN-PLACE (push/splice) statt über das `target`-Model-Signal zu
   * schreiben — `(targetChange)` feuert dabei NIE. Stattdessen auf die tatsächlich feuernden
   * `onMoveToTarget`/`onMoveToSource`/`onMoveAllToTarget`/`onMoveAllToSource`-Events hören und den
   * (von PrimeNG bereits direkt mutierten) gecachten `target`-Stand zurück in `data` schreiben. */
  public syncPicklistValue(field: NgxForm.FormField): void {
    const state = this.getPicklistState(field);
    const readonlyValues = new Set(state.readonlyValues);
    state.source.splice(0, state.source.length, ...state.source.filter(item => !readonlyValues.has(item)));
    state.target.splice(0, state.target.length, ...state.readonlyValues, ...state.target.filter(item => !readonlyValues.has(item)));
    const editableValues = new Set(state.selectValues.map(item => this.getPicklistItemValue(field, item)));
    const preservedValues = field.options?.preserveUnlistedValues
      ? (this.getValue(field) ?? []).filter((value: any) => !editableValues.has(value))
      : [];
    this.setValue(field, [...preservedValues, ...state.target.filter(item => !readonlyValues.has(item)).map((item: any) => this.getPicklistItemValue(field, item))]);
  }

  public onSubmit(): void {
    if (this.submitLock) return;
    this.submitLock = true;
    this.submit.emit(this.data);
    setTimeout(() => this.submitLock = false, 500);
  }

  public onDelete(): void {
    this.delete.emit(this.data);
  }
}
